// /////////////////////////////////////////////////////////////
// [ファイル] BuildingSearchFilters.tsx
// [概要] 建物住所タブの検索 Popover 内フォーム。
//        入力は本コンポーネントのローカル state に閉じ込め、「検索」確定時のみ
//        親の filters を更新する (キー入力ごとの一覧再描画を防ぐ)。
// /////////////////////////////////////////////////////////////
import React, { useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, MenuItem, TextField, Typography } from '@mui/material';
import * as Icons from '../constants/icons';
import type { BuildingFilters } from '../hooks/useBuildingTable';
import {
  STANDARD_CTA_BUTTON_SX,
  STANDARD_TEXT_BUTTON_SX,
} from '../constants/buttonStyles';

interface BuildingSearchFiltersProps {
  filters: BuildingFilters;
  onSearch: (filters: BuildingFilters) => void;
}

export default function BuildingSearchFilters({ filters, onSearch }: BuildingSearchFiltersProps) {
  const [searchWord, setSearchWord] = useState(filters.search_word || '');
  const [source, setSource] = useState(filters.registration_source ?? 'manual');
  const [orphanOnly, setOrphanOnly] = useState(Boolean(filters.orphan_only));

  const handleSearch = () => {
    onSearch({ search_word: searchWord.trim(), registration_source: source, orphan_only: orphanOnly });
  };

  const handleClear = () => {
    setSearchWord('');
    setSource('manual');
    setOrphanOnly(false);
    onSearch({ search_word: '', registration_source: 'manual', orphan_only: false });
  };

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <TextField
        fullWidth
        size="small"
        autoFocus
        label="検索ワード"
        placeholder="建物名・住所・所有者名"
        value={searchWord}
        onChange={(e) => setSearchWord(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSearch();
        }}
      />
      <TextField
        select
        fullWidth
        size="small"
        label="表示対象"
        value={source}
        onChange={(e) => setSource(e.target.value)}
      >
        <MenuItem value="manual">手動登録のみ（既定）</MenuItem>
        <MenuItem value="">すべて（簡易登録を含む）</MenuItem>
        <MenuItem value="customer_auto">簡易登録のみ（顧客住所から自動作成）</MenuItem>
      </TextField>
      <FormControlLabel
        sx={{ m: 0, alignItems: 'flex-start' }}
        control={
          <Checkbox
            size="small"
            checked={orphanOnly}
            onChange={(e) => setOrphanOnly(e.target.checked)}
            sx={{ py: 0 }}
          />
        }
        label={
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            リンクなしのみ
            <Typography component="span" variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem' }}>
              関係者・登記・SFA 案件のいずれからも参照されていない建物
            </Typography>
          </Typography>
        }
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button onClick={handleClear} sx={STANDARD_TEXT_BUTTON_SX}>
          クリア
        </Button>
        <Button onClick={handleSearch} startIcon={<Icons.Search />} sx={STANDARD_CTA_BUTTON_SX}>
          検索
        </Button>
      </Box>
    </Box>
  );
}
